import { useRef } from "react";

import type { RefreshPhase } from "../lib/useRefreshPhase";

const REFRESH_ICON = (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
    <path d="M12.5 1.8v2.7H9.8" />
  </svg>
);
const CHECK_ICON = (
  <svg viewBox="0 0 16 16" aria-hidden="true" className="refresh-check">
    <path d="m3.5 8.5 3 3 6-7" />
  </svg>
);
const ALERT_ICON = (
  <svg viewBox="0 0 16 16" aria-hidden="true" className="refresh-alert">
    <path d="M8 3v6M8 12v.5" />
  </svg>
);

const ANNOUNCEMENT: Record<RefreshPhase, string> = {
  idle: "",
  press: "Updating",
  busy: "Updating",
  done: "Updated",
  failed: "Update failed",
};

export function RefreshButton({
  phase,
  onClick,
  ariaLabel,
  title,
  label,
  className,
}: {
  phase: RefreshPhase;
  onClick: () => void;
  ariaLabel: string;
  title?: string;
  /** Idle text for the labelled variant; the square icon-only button when omitted. */
  label?: string;
  className?: string;
}) {
  const busy = phase === "press" || phase === "busy";
  // The label text stays while the pill shrinks back, otherwise the track collapses at once.
  const lastLabel = useRef("");
  if (busy) lastLabel.current = "Updating…";
  else if (phase === "done") lastLabel.current = "Updated";
  return (
    <>
      <button
        type="button"
        className={className ? `data-refresh ${className}` : "data-refresh"}
        data-phase={phase}
        data-variant={label ? "labelled" : undefined}
        aria-busy={busy ? "true" : "false"}
        aria-label={ariaLabel}
        title={title}
        onClick={onClick}
      >
        {phase === "failed" ? (
          <>
            <span className="refresh-icon refresh-icon-failed" aria-hidden="true">
              {ALERT_ICON}
            </span>
            <span className="refresh-icon refresh-icon-retry" aria-hidden="true">
              {REFRESH_ICON}
            </span>
            <span className="refresh-label" aria-hidden="true">
              {label && <span className="refresh-label-idle">{label}</span>}
              <span className="refresh-label-failed">Update failed</span>
              <span className="refresh-label-retry">Try again</span>
            </span>
          </>
        ) : (
          <>
            <span className="refresh-icon" aria-hidden="true">
              {phase === "done" ? CHECK_ICON : REFRESH_ICON}
            </span>
            <span className="refresh-label" aria-hidden="true">
              {label && <span className="refresh-label-idle">{label}</span>}
              {(phase !== "idle" || !label) && (
                <span className="refresh-label-phase">{lastLabel.current}</span>
              )}
            </span>
          </>
        )}
      </button>
      {/* Outside the button: changes inside an aria-busy element may be deferred until it settles. */}
      <span className="sr-only" aria-live="polite">
        {ANNOUNCEMENT[phase]}
      </span>
    </>
  );
}
